import React from "react";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Badge, Button, Modal } from "react-bootstrap";
import { ScaleLoader } from "react-spinners";
import { resolveAnomalyRuleLabel, ruleCodeShowsExpectedValue } from "../../../core/constants/anomalyRuleCodes";
import {
  ANOMALY_SEVERITY_BADGE,
  formatAnomalySeverityLabel,
  formatAnomalyStatusLabel,
} from "../../../core/constants/anomalySeverity";
import {
  formatAnomalyDisplayDate,
  formatAnomalyDisplayDateTime,
  formatAnomalyMonthYear,
  getAnomalyDisplayPeriod,
} from "./anomalyDateUtils";
import AnomalyInfoTip from "./AnomalyInfoTip";
import { canShowFixAction } from "./anomalyFixUtils";
import "./AnomalyDetailModal.scss";

const STATUS_BADGE = {
  OPEN: "badge-Critical",
  DISMISSED: "badge-Dismissed",
  RESOLVED: "badge-Closed",
};

const formatDetailNumber = (val) => {
  if (val == null || val === "") return "—";
  const n = Number(val);
  return Number.isNaN(n)
    ? String(val)
    : n.toLocaleString(undefined, { maximumFractionDigits: 10 });
};

const formatDetailEnumLabel = (val) =>
  val ? String(val).replace(/_/g, " ") : "—";

const showDetailExpectedValue = (record) =>
  ruleCodeShowsExpectedValue(record?.ruleCode) && record?.expectedValue != null;

const showDetailDeviationPct = (record) =>
  ruleCodeShowsExpectedValue(record?.ruleCode) && record?.deviationPct != null;

const hasExpectedMeasurement = (record) =>
  showDetailExpectedValue(record) || showDetailDeviationPct(record);

const hasComparisonBaselineContent = (comparison, ruleCode) => {
  if (!ruleCodeShowsExpectedValue(ruleCode)) return false;
  return Boolean(
    comparison?.baselineValue != null ||
      comparison?.baselinePeriod ||
      comparison?.deviationPct != null
  );
};

const hasComparisonSection = (comparison, ruleCode) => {
  if (!comparison) return false;
  return Boolean(
    comparison.summary ||
      comparison.typeLabel ||
      comparison.actualValue != null ||
      hasComparisonBaselineContent(comparison, ruleCode)
  );
};

const AnomalyDetailModal = ({
  t,
  show,
  onHide,
  loading,
  record,
  previewRecord,
  onViewTimeSeries,
  onFix,
}) => {
  const navigate = useNavigate();
  const displayRecord = record || previewRecord;

  const isGapRule = displayRecord?.ruleCode === "MISSING_PERIOD_GAP_RULE";
  const displayPeriod = getAnomalyDisplayPeriod(displayRecord);
  const evaluationPeriod = displayRecord?.period;
  const displayPeriodLabel = formatAnomalyMonthYear(displayPeriod);
  const evaluationPeriodLabel = formatAnomalyMonthYear(evaluationPeriod);

  const handleEnterDataForGap = () => {
    if (!displayRecord) return;
    onHide();
    const targetPeriod = displayRecord.missingPeriod || displayPeriod || evaluationPeriod;
    navigate("/company_admin/dataview", {
      state: {
        fromAnomalyGap: true,
        metricId: displayRecord.metricId,
        branchId: displayRecord.branchId,
        datapointId: displayRecord.datapointId,
        targetPeriod,
        // helpful defaults for the data view to focus the missing month if it supports it
        periodFrom: targetPeriod,
        periodTo: targetPeriod,
      },
    });
  };

  const renderSeverityBadge = (severity) => (
    <Badge className="rounded-pill" bg={ANOMALY_SEVERITY_BADGE[severity] || "secondary"}>
      {formatAnomalySeverityLabel(severity)}
    </Badge>
  );

  const renderStatusBadge = (status) => (
    <Badge className="rounded-pill" bg={STATUS_BADGE[status] || "secondary"}>
      {formatAnomalyStatusLabel(status)}
    </Badge>
  );

  const renderDeviationPct = (val) => {
    if (val == null) return "—";
    const cls = val >= 0 ? "deviation-positive" : "deviation-negative";
    return <span className={cls}>{Number(val).toFixed(2)}%</span>;
  };

  const renderContextItem = (label, value, chip = false) => {
    if (!value) return null;
    return (
      <div className="adm-context-item">
        <dt className="adm-context-item-label">{label}</dt>
        <dd className="adm-context-item-value">
          {chip ? (
            <span className="adm-category-chip">{value}</span>
          ) : (
            value
          )}
        </dd>
      </div>
    );
  };

  const renderResolutionSection = (detail) => (
    <section className="adm-panel adm-panel--resolution" data-testid="detail-resolution">
      <div className="adm-panel-head">
        <span className="adm-panel-icon adm-panel-icon--success" aria-hidden="true">✓</span>
        <div>
          <h3 className="adm-panel-title">{t("AnomalyDetection.resolutionSection.label")}</h3>
          {detail.resolvedAt && (
            <p className="adm-panel-subtitle">
              {formatAnomalyDisplayDateTime(detail.resolvedAt)}
              {detail.resolvedBy && (
                <>
                  <span className="adm-meta-sep">·</span>
                  <span className="adm-resolved-by">{detail.resolvedBy}</span>
                </>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="adm-value-flow">
        <div className="adm-value-flow-step adm-value-flow-step--muted">
          <span className="adm-value-flow-label">
            {t("AnomalyDetection.previousValue.label")}
          </span>
          <span className="adm-value-flow-number">
            {formatDetailNumber(detail.previousValue)}
          </span>
        </div>
        <span className="adm-value-flow-arrow" aria-hidden="true">→</span>
        <div className="adm-value-flow-step adm-value-flow-step--success">
          <span className="adm-value-flow-label">
            {t("AnomalyDetection.correctedValue.label")}
          </span>
          <span className="adm-value-flow-number">
            {formatDetailNumber(detail.correctedValue)}
          </span>
        </div>
        {detail.fixUom && (
          <div className="adm-value-flow-step adm-value-flow-step--uom">
            <span className="adm-value-flow-label">
              {t("AnomalyDetection.fixUom.label")}
            </span>
            <span className="adm-value-flow-uom">{detail.fixUom}</span>
          </div>
        )}
      </div>

      {detail.fixRemark && (
        <blockquote className="adm-remark">
          <span className="adm-remark-label">
            {t("AnomalyDetection.fixRemark.label")}
          </span>
          <p>{detail.fixRemark}</p>
        </blockquote>
      )}
    </section>
  );

  const renderMeasurementSection = (detail) => (
    <section className="adm-panel adm-panel--measurement">
      <h3 className="adm-panel-title">{t("AnomalyDetection.measurementSection.label")}</h3>
      <div
        className={`adm-measure-grid${
          hasExpectedMeasurement(detail) ? "" : " adm-measure-grid--compact"
        }`}
      >
        <div className="adm-measure-cell adm-measure-cell--primary">
          <span className="adm-measure-label">
            {isGapRule && evaluationPeriodLabel
              ? t("AnomalyDetection.gapRule.valueAtEvaluation.label", { period: evaluationPeriodLabel })
              : t("AnomalyDetection.currentValue.label")}
          </span>
          <span className="adm-measure-value">{formatDetailNumber(detail.currentValue)}</span>
          {detail.defaultUom && (
            <span className="adm-measure-uom-inline">{detail.defaultUom}</span>
          )}
        </div>
        {showDetailExpectedValue(detail) && (
          <div className="adm-measure-cell">
            <span className="adm-measure-label">
              {t("AnomalyDetection.expectedValue.label")}
            </span>
            <span className="adm-measure-value adm-measure-value--expected">
              {formatDetailNumber(detail.expectedValue)}
            </span>
          </div>
        )}
        {showDetailDeviationPct(detail) && (
          <div className="adm-measure-cell adm-measure-cell--deviation">
            <span className="adm-measure-label">
              {t("AnomalyDetection.deviationPct.label")}
            </span>
            <span
              className={`adm-measure-value adm-measure-value--deviation ${
                detail.deviationPct != null && detail.deviationPct >= 0
                  ? "deviation-positive"
                  : "deviation-negative"
              }`}
            >
              {detail.deviationPct != null
                ? `${Number(detail.deviationPct).toFixed(2)}%`
                : "—"}
            </span>
          </div>
        )}
      </div>
    </section>
  );

  const renderTimelineSection = (detail) => (
    <section className="adm-panel adm-panel--timeline">
      <h3 className="adm-panel-title">{t("AnomalyDetection.timelineSection.label")}</h3>
      <div className="adm-timeline-track">
        <div className="adm-timeline-node">
          <span className="adm-timeline-dot" />
          <div className="adm-timeline-node-content">
            <span className="adm-timeline-label">
              {t("AnomalyDetection.firstDetected.label")}
            </span>
            <span className="adm-timeline-value">
              {detail.firstDetectedAt
                ? formatAnomalyDisplayDateTime(detail.firstDetectedAt)
                : "—"}
            </span>
          </div>
        </div>
        <div className="adm-timeline-connector" aria-hidden="true" />
        <div className="adm-timeline-node">
          <span className="adm-timeline-dot" />
          <div className="adm-timeline-node-content">
            <span className="adm-timeline-label">
              {t("AnomalyDetection.lastDetected.label")}
            </span>
            <span className="adm-timeline-value">
              {detail.lastDetectedAt
                ? formatAnomalyDisplayDateTime(detail.lastDetectedAt)
                : "—"}
            </span>
          </div>
        </div>
        {detail.status === "RESOLVED" && detail.resolvedAt && (
          <>
            <div className="adm-timeline-connector adm-timeline-connector--success" aria-hidden="true" />
            <div className="adm-timeline-node adm-timeline-node--resolved">
              <span className="adm-timeline-dot adm-timeline-dot--success" />
              <div className="adm-timeline-node-content">
                <span className="adm-timeline-label">
                  {t("AnomalyDetection.resolvedAt.label")}
                </span>
                <span className="adm-timeline-value adm-timeline-value--success">
                  {formatAnomalyDisplayDateTime(detail.resolvedAt)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="adm-detection-count">
        <span className="adm-detection-count-label">
          {t("AnomalyDetection.detectionCount.label")}
        </span>
        <span className="adm-detection-count-value">
          {detail.detectionCount ?? "—"}
        </span>
      </div>
    </section>
  );

  const shouldShowRuleInsight = (detail) =>
    Boolean(
      detail.description ||
        detail.ruleDescription ||
        detail.ruleThresholdPct != null ||
        hasComparisonSection(detail.comparison, detail.ruleCode)
    );

  const renderRuleInsight = (detail) => {
    if (!shouldShowRuleInsight(detail)) return null;
    const showComparisonBaseline = ruleCodeShowsExpectedValue(detail.ruleCode);

    return (
      <section className="adm-panel adm-panel--rule">
        <h3 className="adm-panel-title">
          {t("AnomalyDetection.ruleInsightSection.label")}
        </h3>
        {detail.ruleCode && (
          <span className="adm-rule-badge">
            {resolveAnomalyRuleLabel(t, detail.ruleCode)}
          </span>
        )}
        {detail.ruleDescription && (
          <p className="adm-rule-description">{detail.ruleDescription}</p>
        )}
        {!detail.ruleDescription && detail.description && (
          <p className="adm-rule-description">{detail.description}</p>
        )}
        {detail.ruleThresholdPct != null && (
          <p className="adm-rule-threshold">
            {t("AnomalyDetection.thresholdPct.label")}:{" "}
            <strong>{formatDetailNumber(detail.ruleThresholdPct)}%</strong>
          </p>
        )}
        {hasComparisonSection(detail.comparison, detail.ruleCode) && (
          <div className="adm-comparison">
            {detail.comparison.typeLabel && (
              <span className="adm-comparison-type">{detail.comparison.typeLabel}</span>
            )}
            {detail.comparison.summary && (
              <p className="adm-comparison-summary">{detail.comparison.summary}</p>
            )}
            <dl className="adm-comparison-grid">
              {detail.comparison.actualValue != null && (
                <div className="adm-comparison-item">
                  <dt>{t("AnomalyDetection.actualValue.label")}</dt>
                  <dd>{formatDetailNumber(detail.comparison.actualValue)}</dd>
                </div>
              )}
              {showComparisonBaseline && detail.comparison.baselineValue != null && (
                <div className="adm-comparison-item">
                  <dt>{t("AnomalyDetection.expectedValue.label")}</dt>
                  <dd>{formatDetailNumber(detail.comparison.baselineValue)}</dd>
                </div>
              )}
              {showComparisonBaseline && detail.comparison.baselinePeriod && (
                <div className="adm-comparison-item">
                  <dt>{t("AnomalyDetection.period.label")}</dt>
                  <dd>{formatAnomalyDisplayDate(detail.comparison.baselinePeriod)}</dd>
                </div>
              )}
              {showComparisonBaseline && detail.comparison.deviationPct != null && (
                <div className="adm-comparison-item">
                  <dt>{t("AnomalyDetection.deviationPct.label")}</dt>
                  <dd>{renderDeviationPct(detail.comparison.deviationPct)}</dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </section>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="anomaly-detail-modal anomaly-glass-modal"
      data-testid="anomaly-detail-modal"
    >
      <Modal.Header closeButton className="adm-header">
        <div className="adm-header-inner">
          <div className="adm-header-row">
            <span className="adm-eyebrow">{t("AnomalyDetection.detailModal.label")}</span>
            {displayRecord && (
              <div className="adm-header-badges">
                {renderSeverityBadge(displayRecord.severity)}
                {renderStatusBadge(displayRecord.status)}
              </div>
            )}
          </div>
          <Modal.Title as="h2" className="adm-title">
            {displayRecord?.metricName || "—"}
          </Modal.Title>
          {displayRecord && (
            <div className="adm-header-pills">
              {displayPeriod && (
                <span className="adm-pill">
                  {t("AnomalyDetection.period.label")}:{" "}
                  {formatAnomalyDisplayDate(displayPeriod)}
                </span>
              )}
              {isGapRule && (
                <span className="adm-pill adm-pill--gap" title={t("AnomalyDetection.gapRule.missingPeriodBadge.label")}>
                  {t("AnomalyDetection.gapRule.missingPeriodBadge.label")}
                </span>
              )}
              {displayRecord.branchName && (
                <span className="adm-pill">{displayRecord.branchName}</span>
              )}
              {displayRecord.datapointName && (
                <span className="adm-pill adm-pill--muted">{displayRecord.datapointName}</span>
              )}
              {displayRecord.ruleCode && (
                <span className="adm-pill adm-pill--rule">
                  {resolveAnomalyRuleLabel(t, displayRecord.ruleCode)}
                </span>
              )}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="adm-body">
        {loading ? (
          <div className="adm-loading">
            <ScaleLoader color="#3b82f6" />
          </div>
        ) : record ? (
          <div className="adm-content">
            {/* {record.deviationPct != null && (
              <div className="adm-summary-strip">
                <div className="adm-summary-kpi">
                  <span className="adm-summary-kpi-label">
                    {t("AnomalyDetection.deviationPct.label")}
                  </span>
                  <span
                    className={`adm-summary-kpi-value ${
                      record.deviationPct >= 0 ? "deviation-positive" : "deviation-negative"
                    }`}
                  >
                    {Number(record.deviationPct).toFixed(2)}%
                  </span>
                </div>
              </div>
            )} */}

            {record.status === "RESOLVED" && renderResolutionSection(record)}

            {isGapRule && displayPeriodLabel && (
              <div className="adm-gap-header" data-testid="gap-rule-header">
                <div className="adm-gap-title">
                  {displayPeriodLabel} {t("AnomalyDetection.gapRule.notSubmitted.label")}
                </div>
                {evaluationPeriodLabel && (
                  <div className="adm-gap-subtitle">
                    {t("AnomalyDetection.gapRule.detectedWhileChecking.label", {
                      evaluationPeriod: evaluationPeriodLabel,
                    })}
                  </div>
                )}
                {record?.comparison?.summary && (
                  <div className="adm-gap-callout" role="note">
                    <div className="adm-gap-callout-label">
                      {t("AnomalyDetection.gapRule.explanation.label")}
                    </div>
                    <p className="adm-gap-callout-text">{record.comparison.summary}</p>
                  </div>
                )}
              </div>
            )}

            <div className="adm-main-grid">
              <div className="adm-main-col">
                {renderRuleInsight(record)}
              </div>

              <div className="adm-main-col">
                {renderMeasurementSection(record)}
                {renderTimelineSection(record)}
              </div>
            </div>

            <section className="adm-panel adm-panel--context">
              <h3 className="adm-panel-title">{t("AnomalyDetection.contextSection.label")}</h3>
              <dl className="adm-context-grid">
                {renderContextItem(
                  t("AnomalyDetection.regulation.label"),
                  record.regulationName
                )}
                {renderContextItem(t("AnomalyDetection.branch.label"), record.branchName)}
                {renderContextItem(
                  t("AnomalyDetection.datapoint.label"),
                  record.datapointName
                )}
                {renderContextItem(t("AnomalyDetection.topicName.label"), record.topicName)}
                {renderContextItem(
                  t("AnomalyDetection.topicCategory.label"),
                  record.topicCategory ? formatDetailEnumLabel(record.topicCategory) : null,
                  true
                )}
                {renderContextItem(
                  t("AnomalyDetection.category.label"),
                  record.category ? formatDetailEnumLabel(record.category) : null,
                  true
                )}
                {renderContextItem(
                  isGapRule
                    ? t("AnomalyDetection.gapRule.missingPeriod.label")
                    : t("AnomalyDetection.period.label"),
                  displayPeriod ? formatAnomalyDisplayDate(displayPeriod) : null
                )}
                {record.defaultUom &&
                  renderContextItem(
                    t("AnomalyDetection.defaultUom.label"),
                    record.defaultUom
                  )}
              </dl>
            </section>
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="adm-footer">
        {record?.metricId && (
          <Button
            variant="outline-primary"
            className="adm-footer-link"
            onClick={() => {
              onHide();
              onViewTimeSeries(record);
            }}
          >
            {t("AnomalyDetection.viewTimeSeries.label")}
          </Button>
        )}
        <div className="adm-footer-actions">
          {record?.status === "OPEN" && canShowFixAction(record) && (
            <Button
              variant="primary"
              className="sbmBtn"
              onClick={() => {
                onHide();
                onFix(record);
              }}
            >
              {t("AnomalyDetection.fix.label")}
            </Button>
          )}
          {record?.status === "OPEN" && isGapRule && (
            <Button
              variant="primary"
              className="sbmBtn"
              onClick={handleEnterDataForGap}
              title={t("AnomalyDetection.gapRule.enterDataCta.label", {
                displayPeriod: displayPeriodLabel,
              })}
            >
              {t("AnomalyDetection.gapRule.enterDataCta.label", {
                displayPeriod: displayPeriodLabel,
              })}
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>
            {t("Cancel.label")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(AnomalyDetailModal);
